import { useDispatch, useSelector } from "react-redux"
import { useCallback } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { AppDispatch } from "@/store"
import { broadcastLogin, broadcastLogout } from "@/lib/session-sync"
import {
  loginWithCredentials as loginAsync,
  signupWithCredentials as signupAsync,
  validateOtp as validateOtpAsync,
  resendOtp as resendOtpAsync,
  logoutCurrentUser,
  fetchUserProfile as fetchProfileAsync,
  clearError,
  logout as logoutAction,
  selectAuthContextData,
} from "@/modules/auth/store/authSlice"
import { selectCurrentOrganization } from "@/modules/organizations/store/organizationSlice"
import { clearAllHubConnections } from "@/modules/recruitment/applications/infrastructure/signalr-service"
import { toast } from "sonner"
import { AppRole } from "@/modules/auth/constants/app-role"
import { AuthErrorPayload } from "../entities/AuthError"

const heldOrganizationRoles = (roles?: string[] | null, role?: string | null): string[] =>
  roles && roles.length > 0 ? roles : role ? [role] : []

export function useAuth() {
  const dispatch = useDispatch<AppDispatch>()
  const queryClient = useQueryClient()

  const auth = useSelector(selectAuthContextData)
  const currentOrganization = useSelector(selectCurrentOrganization)

  const purgeSessionState = useCallback(() => {
    queryClient.clear()
    clearAllHubConnections()
    localStorage.clear()
    sessionStorage.clear()
  }, [queryClient])

  const loginWithCredentials = async (
    email: string,
    password: string,
    inviteToken?: string,
    captchaToken?: string,
  ) => {
    try {
      purgeSessionState()
      dispatch(logoutAction())

      const result = await dispatch(
        loginAsync({ email, password, inviteToken, captchaToken }),
      ).unwrap()
      broadcastLogin(email)
      toast.success("Login successful!")
      return result
    } catch (err: unknown) {
      const authErr = err as AuthErrorPayload
      toast.error(authErr.error ?? "Login failed")
      throw { error: authErr.error, errorCode: authErr.errorCode }
    }
  }

  const signupWithCredentials = async (firstName: string, lastName: string, email: string, password: string, acceptedTermsVersion?: string, acceptedPrivacyVersion?: string, captchaToken?: string) => {
    try {
      const result = await dispatch(
        signupAsync({ userName: email, password, firstName, lastName, acceptedTermsVersion, acceptedPrivacyVersion, captchaToken }),
      ).unwrap()

      toast.success("Signup successful!")
      return result
    } catch (error) {
      toast.error(error as string)
      throw error
    }
  }

  const validateOtp = async (email: string, otp: string, captchaToken?: string) => {
    try {
      const result = await dispatch(validateOtpAsync({ email, otp, captchaToken })).unwrap()

      toast.success("OTP verified successfully!")
      return result
    } catch (error) {
      toast.error(error as string)
      throw error
    }
  }

  const resendOtp = async (email: string, captchaToken?: string) => {
    try {
      const result = await dispatch(resendOtpAsync({ email, captchaToken })).unwrap()

      toast.success("OTP resent successfully!")
      return result
    } catch (error) {
      toast.error(error as string)
      throw error
    }
  }

  const logout = () => {
    purgeSessionState()
    broadcastLogout()
    return dispatch(logoutCurrentUser())
  }

  const fetchUserProfile = useCallback(async () => {
    return dispatch(fetchProfileAsync()).unwrap()
  }, [dispatch])

  const clearAuthError = () => {
    dispatch(clearError())
  }

  const hasRole = (role: AppRole | AppRole[]) => {
    const hasGlobal = Array.isArray(role)
      ? role.some((r) => auth.roles.includes(r))
      : auth.roles.includes(role)

    if (hasGlobal) return true

    const orgRoles = new Set<string>([
      ...heldOrganizationRoles(auth.user?.user?.organizationRoles, auth.user?.user?.organizationRole),
      ...heldOrganizationRoles(currentOrganization?.userRoles, currentOrganization?.userRole),
    ])
    const wanted = Array.isArray(role) ? role : [role]

    return wanted.some((r) => orgRoles.has(r))
  }

  const hasPermission = (permission: string | string[]) => {
    if (!auth.permissions || auth.permissions.length === 0) return false
    return Array.isArray(permission)
      ? permission.every((p) => auth.permissions.includes(p))
      : auth.permissions.includes(permission)
  }

  return {
    ...auth,
    loginWithCredentials,
    signupWithCredentials,
    validateOtp,
    resendOtp,
    fetchUserProfile,
    logout,
    clearError: clearAuthError,
    hasRole,
    hasPermission,
  }
}
